import { Link, NavLink } from "react-router-dom";
import { Show, UserButton, useClerk } from "@clerk/react";
import { useState } from "react";
import { Menu, X, Crown, LogOut, MapPin, Share2, MessageSquare } from "lucide-react";
import { InstallButton } from "./InstallApp";
import { useUserLocation } from "../lib/location";
import { HeartLink, NotificationBell } from "./HeaderIcons";
import ShareButton from "./ShareButton";
import PromoButtons from "./PromoButtons";

const LINKS = [
  { to: "/cars", label: "Browse Cars", id: "nav-browse" },
  { to: "/cars?seller_type=private", label: "Private Owners", id: "nav-private" },
  { to: "/dealers", label: "Dealer Stores", id: "nav-dealers" },
  { to: "/cities", label: "Cities", id: "nav-cities" },
];

export const Logo = () => (
  <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 min-w-0">
    <span className="w-8 h-8 sm:w-9 sm:h-9 shrink-0 rounded-full flex items-center justify-center" style={{ background: "var(--gold-grad)" }}>
      <Crown className="w-4 h-4 text-black" strokeWidth={2.4} />
    </span>
    <span className="font-display text-xl sm:text-2xl font-semibold tracking-tight whitespace-nowrap">
      <span className="gold-text">CarSale</span>Market<span className="text-[#9CA3AF] text-sm sm:text-base">.com</span>
    </span>
  </Link>
);

const LogoutButton = ({ testid, className = "" }) => {
  const { signOut } = useClerk();
  return (
    <button onClick={() => signOut({ redirectUrl: "/" })} className={`flex items-center gap-1.5 text-sm text-gray-300 hover:text-[#E5C158] transition-colors ${className}`} data-testid={testid}>
      <LogOut className="w-4 h-4" />Logout
    </button>
  );
};

const CityChip = ({ testid, className = "" }) => {
  const { location, setAskOpen } = useUserLocation();
  return (
    <button onClick={() => setAskOpen(true)} className={`flex items-center gap-1 text-xs text-gray-300 hover:text-[#E5C158] border border-white/10 hover:border-[#D4AF37]/50 rounded-full px-2.5 py-1 transition-colors max-w-[220px] ${className}`} data-testid={testid}>
      <MapPin className="w-3.5 h-3.5 text-[#D4AF37] shrink-0" /><span className="truncate">{location?.city || "Select city"}</span>
    </button>
  );
};

export default function Header() {
  const [open, setOpen] = useState(false);
  const linkCls = ({ isActive }) => `text-sm transition-colors duration-200 whitespace-nowrap ${isActive ? "text-[#F5E08F]" : "text-gray-300 hover:text-[#E5C158]"}`;
  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-black/80 border-b gold-line" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 h-14 lg:h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0"><Logo /></div>
        <nav className="hidden lg:flex items-center gap-4 xl:gap-5">
          {LINKS.map((l) => <NavLink key={l.id} to={l.to} className={linkCls} data-testid={l.id} end>{l.label}</NavLink>)}
          <Show when="signed-in"><NavLink to="/chat" className={linkCls} data-testid="nav-chat">Chats</NavLink><NavLink to="/dashboard" className={linkCls} data-testid="nav-dashboard">Dashboard</NavLink></Show>
        </nav>
        <div className="hidden lg:flex items-center gap-2 shrink-0">
          <CityChip testid="header-city-chip" className="max-w-[130px]" />
          <ShareButton className="w-9 h-9 rounded-full border border-white/10 hover:border-[#D4AF37]/60 flex items-center justify-center text-gray-200" testid="header-share-app"><Share2 className="w-4 h-4" /></ShareButton>
          <NotificationBell />
          <HeartLink />
          <InstallButton testid="header-install-btn" compact />
          <Link to="/sell" className="btn-gold text-sm !py-2" data-testid="header-sell-btn">Sell</Link>
          <Show when="signed-out"><Link to="/sign-in" className="btn-outline text-sm !py-2" data-testid="header-signin-btn">Sign In</Link></Show>
          <Show when="signed-in"><UserButton /><LogoutButton testid="header-logout-btn" /></Show>
        </div>
        <div className="lg:hidden flex items-center gap-2 shrink-0">
          <NotificationBell testid="m-header-bell-btn" />
          <HeartLink testid="m-header-saved-btn" />
          <button className="w-9 h-9 flex items-center justify-center text-[#E5C158]" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="mobile-menu-toggle">{open ? <X /> : <Menu />}</button>
        </div>
      </div>
      <div className="lg:hidden px-3 pb-2 -mt-0.5 flex items-center justify-between gap-2" data-testid="m-header-location-row"><CityChip testid="m-header-city-chip" className="max-w-[110px]" /><PromoButtons prefix="m-" btnClass="!px-2 !text-[10px]" /></div>
      <div className="hidden lg:flex justify-end px-8 pb-2 -mt-1 max-w-7xl mx-auto"><PromoButtons prefix="header-" /></div>
      {open && (
        <div className="lg:hidden border-t gold-line bg-black/95 px-6 py-5 flex flex-col gap-4 max-h-[75vh] overflow-y-auto" onClick={() => setOpen(false)}>
          {LINKS.map((l) => <NavLink key={l.id} to={l.to} className={linkCls} data-testid={`m-${l.id}`}>{l.label}</NavLink>)}
          <Show when="signed-in">
            <NavLink to="/chat" className={linkCls} data-testid="m-nav-chat"><span className="flex items-center gap-2"><MessageSquare className="w-4 h-4" />Chats & Offers</span></NavLink>
            <NavLink to="/dashboard" className={linkCls} data-testid="m-nav-dashboard">Dashboard</NavLink>
          </Show>
          <Link to="/sell" className="btn-gold text-sm" data-testid="m-sell-btn">Sell Your Car</Link>
          <div onClick={(e) => e.stopPropagation()}><ShareButton className="btn-outline text-sm w-full" testid="m-share-app"><Share2 className="w-4 h-4" />Share App</ShareButton></div>
          <InstallButton testid="m-install-btn" />
          <Show when="signed-out"><Link to="/sign-in" className="btn-outline text-sm" data-testid="m-signin-btn">Sign In</Link></Show>
          <Show when="signed-in"><div className="flex items-center justify-between"><div className="flex items-center gap-3 text-sm text-gray-400"><UserButton /> Account</div><LogoutButton testid="m-logout-btn" /></div></Show>
        </div>
      )}
    </header>
  );
}
